import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import TTSPlayer from "@/components/TTSPlayer";
import AiTeacher from "@/components/AiTeacher";
import Checkpoint from "@/components/Checkpoint";
import HighlightContainer from "@/components/HighlightContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, Lightbulb, Bookmark, BookmarkCheck, Download, MessageCircle, Lock, PlayCircle, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function Paragraph({ body }) {
  const paragraphs = body.split(/\n\n+/);
  return (
    <div className="space-y-4">
      {paragraphs.map((p, i) => {
        const lines = p.split(/\n/);
        const isBullets = lines.length > 1 && lines.every(l => /^\s*[•\-]\s/.test(l));
        const isNumbered = lines.length > 1 && lines.every(l => /^\s*\d+[.)]\s/.test(l));
        if (isBullets || isNumbered) {
          const Tag = isNumbered ? "ol" : "ul";
          return (
            <Tag key={i} className="space-y-2 list-none pl-0">
              {lines.map((l, li) => {
                const marker = isNumbered ? l.match(/^\s*(\d+)[.)]\s/)?.[1] + "." : "•";
                const content = l.replace(/^\s*(?:[•\-]|\d+[.)])\s*/, "");
                return (
                  <li key={li} className="flex gap-3 text-base leading-relaxed">
                    <span className={`${isNumbered ? "font-mono text-sm text-muted-foreground" : "text-accent"} mt-0.5 shrink-0`}>{marker}</span>
                    <span>{renderInline(content)}</span>
                  </li>
                );
              })}
            </Tag>
          );
        }
        return (
          <p key={i} className="text-base leading-relaxed">
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(l)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export default function Chapter() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const chapterId = parseInt(id, 10);
  const { user } = useAuth();
  const [chapter, setChapter] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("read");
  const [pageIdx, setPageIdx] = useState(0);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [highlights, setHighlights] = useState([]);
  const [locked, setLocked] = useState(null); // null unknown, or {detail}
  const nav = useNavigate();

  const loadBookmarks = async () => {
    try {
      const r = await api.get("/bookmarks");
      const forThis = r.data.filter(b => b.chapter_id === chapterId).map(b => b.section_idx);
      setBookmarks(new Set(forThis));
    } catch (e) { /* ignore */ }
  };

  const loadHighlights = async () => {
    try {
      const r = await api.get(`/highlights?chapter_id=${chapterId}`);
      setHighlights(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get(`/course/${chapterId}`).then(r => {
      setChapter(r.data);
      setAnswers({});
      setResult(null);
      setMode("read");
      setLocked(null);
      const initial = parseInt(searchParams.get("section") || "0", 10);
      setPageIdx(Number.isFinite(initial) && initial >= 0 ? initial : 0);
      window.scrollTo(0, 0);
    }).catch(err => {
      if (err?.response?.status === 402) {
        setLocked({ detail: err.response.data?.detail || "This chapter is locked. Enroll to continue." });
        setChapter(null);
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load chapter");
      }
    });
    loadBookmarks();
    loadHighlights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const fullText = useMemo(() => {
    if (!chapter) return "";
    return [chapter.title, chapter.summary, ...chapter.sections.map(s => `${s.heading}. ${s.body.replace(/\*\*/g, "")}`)].join(". ");
  }, [chapter]);

  const pageText = useMemo(() => {
    if (!chapter) return "";
    const s = chapter.sections[pageIdx];
    if (!s) return "";
    return `${s.heading}. ${s.body.replace(/\*\*/g, "")}. ${(s.key_facts || []).join(". ")}`;
  }, [chapter, pageIdx]);

  if (locked) return (
    <Layout>
      <div className="max-w-2xl mx-auto p-6 lg:p-12">
        <Link to="/course" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="paywall-back-course">
          <ArrowLeft size={14} /> All chapters
        </Link>
        <div className="border border-accent rounded-md p-10 bg-accent/5 text-center" data-testid="paywall-block">
          <Lock size={48} className="mx-auto text-accent mb-4" />
          <div className="label-caps mb-2 text-accent">Chapter locked</div>
          <h1 className="font-serif text-4xl lg:text-5xl mb-4">Enroll to keep learning.</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">{locked.detail}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" data-testid="paywall-see-plans"><Link to="/pricing">See plans</Link></Button>
            <Button variant="outline" asChild data-testid="paywall-preview-ch1"><Link to="/course/1">Read chapter 1 free</Link></Button>
          </div>
        </div>
      </div>
    </Layout>
  );

  if (!chapter) return <Layout><div className="p-10">Loading…</div></Layout>;

  const totalPages = chapter.sections.length;
  const currentSection = chapter.sections[pageIdx];
  const readProgress = Math.round(((pageIdx + 1) / totalPages) * 100);
  const isBookmarked = bookmarks.has(pageIdx);

  const nextPage = () => {
    if (pageIdx < totalPages - 1) {
      setPageIdx(pageIdx + 1);
      window.scrollTo({top: 0, behavior: "smooth"});
    } else {
      setMode("quiz");
      window.scrollTo({top: 0, behavior: "smooth"});
    }
  };
  const prevPage = () => {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
      window.scrollTo({top: 0, behavior: "smooth"});
    }
  };

  const toggleBookmark = async () => {
    try {
      const r = await api.post("/bookmarks", { chapter_id: chapterId, section_idx: pageIdx });
      const next = new Set(bookmarks);
      if (r.data.status === "added") { next.add(pageIdx); toast.success("Section bookmarked"); }
      else { next.delete(pageIdx); toast.success("Bookmark removed"); }
      setBookmarks(next);
    } catch (e) { toast.error("Bookmark failed"); }
  };

  const downloadStudyGuide = async () => {
    try {
      const r = await api.get(`/study-guide/${chapterId}`);
      const d = r.data;
      if (!d.facts || d.facts.length === 0) {
        toast.info("This chapter has no key facts to compile. Try a different chapter.");
        return;
      }
      const win = window.open("", "_blank");
      if (!win) { toast.error("Popup blocked"); return; }
      // Group facts by heading
      const groups = {};
      d.facts.forEach(f => { (groups[f.heading] = groups[f.heading] || []).push(f.fact); });
      win.document.write(`<!doctype html><html><head><title>Study Guide - ${d.title}</title>
        <style>
          body { font-family: Georgia, serif; margin: 0; padding: 40px; background: #f6f5ee; color: #111; }
          .card { max-width: 800px; margin: 0 auto; background: #fffdf4; border: 1px solid #ccc; padding: 40px; }
          .brand { letter-spacing: 8px; text-transform: uppercase; font-size: 11px; color: hsl(var(--primary, 265 55% 28%)); }
          h1 { font-size: 40px; margin: 8px 0 0; font-weight: 500; }
          .cat { font-size: 12px; text-transform: uppercase; letter-spacing: 3px; color: #666; margin-top: 6px; }
          .summary { color: #444; margin: 12px 0 24px; }
          h2 { font-size: 18px; margin: 20px 0 6px; color: #222; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          ul { margin: 4px 0 12px 20px; padding: 0; }
          li { margin-bottom: 4px; font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 13px; }
          .footer { margin-top: 40px; font-size: 10px; color: #999; text-align: center; letter-spacing: 2px; text-transform: uppercase; }
          strong { font-weight: 700; }
        </style></head><body>
        <div class="card">
          <div class="brand">DyNamic Tax School</div>
          <h1>Chapter ${d.chapter_id} - ${d.title}</h1>
          <div class="cat">${d.category} - Study Guide</div>
          <div class="summary">${d.summary}</div>
          ${Object.entries(groups).map(([h, arr]) => `<h2>${h}</h2><ul>${arr.map(a => `<li>${a.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ul>`).join("")}
          <div class="footer">Print · One-page cheat sheet</div>
        </div>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`);
      win.document.close();
    } catch (e) { toast.error("Could not build study guide"); }
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length !== chapter.quiz.length) {
      toast.error("Please answer every question.");
      return;
    }
    setSubmitting(true);
    try {
      const arr = chapter.quiz.map((_, i) => answers[i]);
      const r = await api.post("/quiz/submit", { chapter_id: chapterId, answers: arr });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed with ${r.data.score}%!`);
      else toast.error(`Scored ${r.data.score}%. You need 80% to pass this chapter.`);
    } catch (e) {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetChapter = async () => {
    try {
      await api.delete(`/progress/${chapterId}`);
      toast.success("Chapter progress reset");
      setResult(null); setAnswers({}); setMode("read"); setPageIdx(0);
    } catch (e) { toast.error("Reset failed"); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="flex items-center justify-between">
          <Link to="/course" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-course">
            <ArrowLeft size={14} /> All chapters
          </Link>
          <div className="flex items-center gap-2">
            <Link to={`/questions`} data-testid="chapter-ask-instructor-link">
              <Button variant="ghost" size="sm">
                <MessageCircle size={14} className="mr-2" /> Ask instructor
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={downloadStudyGuide} data-testid="download-study-guide-btn">
              <Download size={14} className="mr-2" /> Study guide
            </Button>
          </div>
        </div>

        <header className="mt-6 mb-8 border-b border-border pb-8">
          <div className="label-caps mb-3">Chapter {chapter.id} · {chapter.category}</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none">{chapter.title}</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{chapter.summary}</p>
          {chapter.video_url && mode === "read" && (
            <div className="mt-6" data-testid="chapter-intro-video">
              <div className="label-caps text-[10px] mb-2 flex items-center gap-1"><PlayCircle size={12} /> Chapter intro</div>
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${chapter.video_url}`}
                controls
                preload="metadata"
                className="w-full rounded-md border border-border bg-black aspect-video"
                data-testid="chapter-video-player"
              />
            </div>
          )}
          {mode === "read" && (
            <div className="mt-6 flex items-center gap-4" data-testid="chapter-progress-bar">
              <div className="label-caps text-[10px] whitespace-nowrap">Section {pageIdx + 1} of {totalPages}</div>
              <Progress value={readProgress} className="flex-1 max-w-sm" />
              <div className="text-xs text-muted-foreground hidden md:block">
                {pageIdx < totalPages - 1 ? `${totalPages - pageIdx - 1} more to go` : "Last section — quiz next"}
              </div>
            </div>
          )}
        </header>

        {mode === "read" ? (
          <article>
            <AnimatePresence mode="wait">
              <motion.section
                key={pageIdx}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
                className="mb-10"
                data-testid={`chapter-page-${pageIdx}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="label-caps">§ {pageIdx + 1}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleBookmark}
                    data-testid="toggle-bookmark-btn"
                    className={isBookmarked ? "text-accent" : ""}
                  >
                    {isBookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                    <span className="ml-1 text-xs">{isBookmarked ? "Saved" : "Bookmark"}</span>
                  </Button>
                </div>
                <h2 className="font-serif text-3xl lg:text-4xl mb-5">{currentSection.heading}</h2>
                <HighlightContainer
                  chapterId={chapterId}
                  sectionIdx={pageIdx}
                  highlights={highlights.filter(h => h.section_idx === pageIdx)}
                  onChange={loadHighlights}
                >
                  <Paragraph body={currentSection.body} />
                  {currentSection.key_facts && currentSection.key_facts.length > 0 && (
                    <div className="mt-8 rounded-md border-l-4 border-accent bg-secondary/50 p-5" data-testid="key-facts-callout">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb size={16} className="text-accent" />
                        <div className="label-caps text-foreground">Key facts to memorize</div>
                      </div>
                      <ul className="space-y-1.5">
                        {currentSection.key_facts.map((f, i) => (
                          <li key={i} className="flex gap-2 text-sm">
                            <span className="text-accent font-mono">·</span>
                            <span>{renderInline(f)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </HighlightContainer>
                {currentSection.checkpoint && (
                  <Checkpoint
                    chapterId={chapterId}
                    sectionIdx={pageIdx}
                    checkpoint={currentSection.checkpoint}
                  />
                )}
                {chapterId === 2 && pageIdx === totalPages - 1 && user?.role === "student" && user?.access_status !== "active" && (
                  <div className="mt-10 rounded-md border-2 border-accent bg-accent/5 p-6 lg:p-8 relative overflow-hidden" data-testid="upsell-card-ch2">
                    <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-accent/10 pointer-events-none" />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-accent" />
                        <div className="label-caps text-accent">You're 2 chapters in</div>
                      </div>
                      <h3 className="font-serif text-3xl lg:text-4xl leading-tight mb-3">Unlock the rest of the course.</h3>
                      <p className="text-sm lg:text-base text-muted-foreground mb-5 max-w-lg">
                        Chapters 3–13 dive into income types, deductions, credits, self-employment, small businesses, and the full final exam. Pick a plan and you'll have it all in one payment.
                      </p>
                      <div className="grid sm:grid-cols-3 gap-2 mb-6 max-w-md">
                        <div className="text-center px-3 py-2 rounded bg-card border border-border">
                          <div className="font-serif text-2xl leading-none">11</div>
                          <div className="label-caps text-[9px] mt-1">More chapters</div>
                        </div>
                        <div className="text-center px-3 py-2 rounded bg-card border border-border">
                          <div className="font-serif text-2xl leading-none">4</div>
                          <div className="label-caps text-[9px] mt-1">1040 practices</div>
                        </div>
                        <div className="text-center px-3 py-2 rounded bg-card border border-border">
                          <div className="font-serif text-2xl leading-none">1</div>
                          <div className="label-caps text-[9px] mt-1">Certificate</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Button asChild size="lg" data-testid="upsell-see-plans">
                          <Link to="/pricing">Unlock everything <ArrowRight size={14} className="ml-2" /></Link>
                        </Button>
                        <Button variant="ghost" size="lg" onClick={() => setMode("quiz")} data-testid="upsell-finish-quiz">
                          Finish the free chapter 2 quiz first
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.section>
            </AnimatePresence>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-border">
              <Button variant="outline" onClick={prevPage} disabled={pageIdx === 0} data-testid="prev-page-btn" className="shrink-0">
                <ArrowLeft size={14} className="mr-2" /> Previous
              </Button>
              <div className="hidden sm:flex flex-wrap gap-1.5">
                {chapter.sections.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`page-dot-${i}`}
                    onClick={() => { setPageIdx(i); window.scrollTo({top: 0, behavior: "smooth"}); }}
                    className={`h-2 w-8 rounded-full transition-colors ${i === pageIdx ? "bg-primary" : i < pageIdx ? "bg-primary/40" : "bg-border"}`}
                    aria-label={`Go to page ${i + 1}`}
                  />
                ))}
              </div>
              <Button onClick={nextPage} data-testid={pageIdx === totalPages - 1 ? "start-quiz-btn" : "next-page-btn"} className="w-full sm:w-auto sm:min-w-[220px]">
                {pageIdx === totalPages - 1 ? "Take the chapter quiz" : (
                  <span className="flex flex-col items-start leading-tight text-left">
                    <span className="text-[10px] opacity-70 label-caps">Up next · §{pageIdx + 2}</span>
                    <span className="truncate max-w-[180px]">{chapter.sections[pageIdx + 1]?.heading}</span>
                  </span>
                )} <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>

            {/* Section index — always visible so students know how much is left */}
            <div className="mt-8 border border-border rounded-md p-4 bg-secondary/40" data-testid="section-index">
              <div className="label-caps text-[10px] mb-2">All sections in this chapter ({totalPages})</div>
              <ol className="space-y-1">
                {chapter.sections.map((s, i) => (
                  <li key={i}>
                    <button
                      onClick={() => { setPageIdx(i); window.scrollTo({top: 0, behavior: "smooth"}); }}
                      data-testid={`section-jump-${i}`}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 hover:bg-background ${i === pageIdx ? "bg-background font-medium" : "text-muted-foreground"}`}
                    >
                      <span className="font-mono text-[10px] shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="truncate min-w-0">{s.heading}</span>
                      {i === pageIdx && <span className="ml-auto label-caps text-[9px] text-accent shrink-0">Now reading</span>}
                    </button>
                  </li>
                ))}
              </ol>
            </div>

            {/* Bottom spacer so the sticky TTS pill doesn't overlap pagination */}
            <div className="h-24" aria-hidden />

            <TTSPlayer text={pageText} />
          </article>
        ) : (
          <div>
            <h2 className="font-serif text-3xl mb-6">Chapter Quiz</h2>
            <p className="text-sm text-muted-foreground mb-6">Answer every question. 80% or higher passes the chapter.</p>
            <div className="space-y-6">
              {chapter.quiz.map((q, i) => (
                <Card key={i} className="p-6" data-testid={`quiz-question-${i}`}>
                  <div className="label-caps mb-2">Q{i + 1}</div>
                  <div className="font-medium mb-4">{q.q}</div>
                  <div className="grid gap-2">
                    {q.options.map((opt, oi) => {
                      const selected = answers[i] === oi;
                      const correctIdx = result?.correct_answers?.[i];
                      const isCorrect = result && oi === correctIdx;
                      const isWrong = result && selected && !result.results[i];
                      return (
                        <button
                          key={oi}
                          type="button"
                          data-testid={`quiz-${i}-option-${oi}`}
                          onClick={() => !result && setAnswers(a => ({ ...a, [i]: oi }))}
                          disabled={!!result}
                          className={`text-left px-4 py-3 rounded-md border transition-all ${
                            isCorrect ? "border-primary bg-primary/10" :
                            isWrong ? "border-destructive bg-destructive/10" :
                            selected ? "border-primary bg-secondary" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-muted-foreground">{String.fromCharCode(65 + oi)}</span>
                            <span className="flex-1">{opt}</span>
                            {isCorrect && <CheckCircle2 size={16} className="text-primary" />}
                            {isWrong && <XCircle size={16} className="text-destructive" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
            {!result ? (
              <div className="flex gap-3 mt-8">
                <Button data-testid="submit-quiz-btn" size="lg" onClick={submitQuiz} disabled={submitting}>
                  Submit quiz
                </Button>
                <Button variant="ghost" onClick={() => setMode("read")}>Back to reading</Button>
              </div>
            ) : (
              <Card className="p-6 mt-8" data-testid="quiz-result">
                <div className="label-caps mb-2">Result</div>
                <div className="font-serif text-4xl">{result.score}%</div>
                <div className="text-sm text-muted-foreground mt-1">{result.correct} of {result.total} correct — {result.passed ? "Passed" : "Not passed (need 80%)"}</div>
                <div className="flex flex-wrap gap-3 mt-6">
                  {chapterId < 13 && <Button data-testid="next-chapter-btn" onClick={() => nav(`/course/${chapterId + 1}`)}>Next chapter <ArrowRight size={14} className="ml-2" /></Button>}
                  <Button variant="outline" onClick={resetChapter} data-testid="retry-chapter-btn">Reset & retry chapter</Button>
                </div>
              </Card>
            )}
          </div>
        )}

        <AiTeacher chapterId={chapterId} />
      </div>
    </Layout>
  );
}
